const React = require("react");

// Kept in sync with the real, hydrated link set so the no-JS/crawler HTML
// and the post-hydration DOM expose the same <a href> targets. Source of
// truth for each group:
//   - PRIMARY_NAV / TRAILING_NAV: src/components/Headers/Navbar.js top-level links
//   - RESEARCH_MENU: NavbarMenuItemsArray.js firstColumnResearchItem + secondColumnResearchItem
//   - JALDI_LAB_MENU: NavbarMenuItemsArray.js firstColumnJaldiLabItem + secondColumnJaldiLabItem
//   - JALDI_PORTAL_MENU: NavbarMenuItemsArray.js portalNavItems (non-header entries)
//   - FOOTER_LINKS / FOOTER_SOCIAL / FOOTER_MISC: src/components/Footer/Footer.js
// If those source files change their paths, update the lists below too.
const PRIMARY_NAV = [
  ["Home", "/"],
  ["About", "/about"],
];

const RESEARCH_MENU = [
  ["Overview of Projects", "/overview-projects"],
  ["Capacity and Performance of Justice Delivery Systems", "/overview-projects/capacity-performance"],
  ["Technology in Dispute Resolution", "/overview-projects/technology-resolution"],
  ["Court Infrastructure", "/overview-projects/court-infrastructure"],
  ["Diversity in the Judiciary", "/overview-projects/diversity-judiciary"],
  ["Making Indian Judiciary Future-Ready", "/overview-projects/making-indian-judiciary"],
  ["Engagements", "/overview-projects/engagements"],
  ["Other Publications", "/overview-projects/publications"],
];

const JALDI_LAB_MENU = [
  ["JALDI Lab", "/jaldilab"],
  ["Projects", "/jaldilab/projects"],
  ["Notes from the Field", "/jaldilab/notes-from-field"],
  ["Collaborators", "/jaldilab/collaborators"],
];

const JALDI_PORTAL_MENU = [
  ["All Portals", "/jaldi-portal"],
  ["Judicial Vacancy", "/jaldi-portal/judicial-vacancy"],
  ["Women in the Higher Judiciary", "/jaldi-portal/women-in-judiciary"],
  ["Changes in Gender Diversity in the District Judiciary", "/jaldi-portal/women-in-judiciary/gender-diversity-portal"],
  ["Constitution Bench Pendency Project", "/constitution-bench"],
  ["Constitution Bench Time Tracker", "/jaldi-portal/constitution-bench-time-tracker"],
];

const TRAILING_NAV = [
  ["Events", "/events/"],
  ["Subscribe", "/subscribe"],
  ["Videos", "/videos"],
];

const FOOTER_LINKS = [
  ["JALDI Lab", "/jaldilab"],
  ["JALDI Portals", "/jaldi-portal"],
  ["Events", "/events"],
];

const FOOTER_SOCIAL = [
  ["Twitter", "https://twitter.com/vidhi_india?lang=en"],
  ["Facebook", "https://www.facebook.com/vidhilegalpolicy/"],
  ["LinkedIn", "https://www.linkedin.com/company/vidhi-centre-for-legal-policy/"],
  ["Instagram", "https://www.instagram.com/vidhi_india/"],
];

const VIDHI_WEBSITE_URL = "https://vidhilegalpolicy.in/";
const CONTACT_EMAIL = "jaldi@vidhilegalpolicy.in";

const navLink = ([label, href]) =>
  React.createElement("a", { href, key: href }, label);

// The wrapper divs/classNames below exist purely for the inline CSS in
// page-ssr.js to hang branded styling off of (a red header bar, a footer
// band, card-styled figures) — they add no links or text, so they don't
// change the <a href>/<img src> sets the SSR/hydration parity fixes above
// rely on matching. This can't reuse the real MUI theme (that's runtime
// CSS-in-JS, unavailable in raw server HTML — see SSR_AUDIT.md), but a
// hand-styled approximation is much closer to "branded page" than bare
// unstyled HTML for a no-JS/crawler view.
const SiteChrome = ({ children }) => React.createElement(
  React.Fragment,
  null,
  React.createElement(
    "header",
    { className: "ssr-site-header", "data-ssr-section": "site-header" },
    React.createElement(
      "div",
      { className: "ssr-header-inner" },
      React.createElement("a", { className: "ssr-brand", href: "/", "aria-label": "JALDI home" }, "JALDI"),
      React.createElement(
        "nav",
        { className: "ssr-nav", "aria-label": "Primary navigation" },
        [
          ...PRIMARY_NAV.map(navLink),
          React.createElement(
            "span",
            { key: "research-menu", "aria-label": "Research" },
            RESEARCH_MENU.map(navLink),
          ),
          React.createElement(
            "span",
            { key: "jaldi-lab-menu", "aria-label": "JALDI Lab" },
            JALDI_LAB_MENU.map(navLink),
          ),
          React.createElement(
            "span",
            { key: "jaldi-portal-menu", "aria-label": "JALDI Portals" },
            JALDI_PORTAL_MENU.map(navLink),
          ),
          ...TRAILING_NAV.map(navLink),
        ],
      ),
    ),
  ),
  children,
  React.createElement(
    "footer",
    { className: "ssr-site-footer", "data-ssr-section": "site-footer" },
    React.createElement(
      "div",
      { className: "ssr-footer-inner" },
      React.createElement("p", { className: "ssr-footer-brand" }, "JALDI — Vidhi Centre for Legal Policy"),
      React.createElement(
        "p",
        null,
        "Contact us at ",
        React.createElement("a", { href: `mailto:${CONTACT_EMAIL}` }, CONTACT_EMAIL),
      ),
      React.createElement(
        "nav",
        { className: "ssr-nav", "aria-label": "Footer navigation" },
        [
          ...FOOTER_LINKS.map(navLink),
          React.createElement("a", { href: VIDHI_WEBSITE_URL, key: "vidhi-website" }, "Vidhi Centre for Legal Policy"),
          ...FOOTER_SOCIAL.map(navLink),
          React.createElement("a", { href: "/privacy-policy", key: "/privacy-policy" }, "Privacy Policy"),
          React.createElement("a", { href: "/terms-conditions", key: "/terms-conditions" }, "Terms and Conditions"),
        ],
      ),
    ),
  ),
);

const SsrContent = ({ model }) => {
  if (model.kind === "report") {
    return React.createElement(
      SiteChrome,
      null,
      React.createElement(
        "article",
        { className: "initial-report", "data-server-rendered-report": "true" },
        React.createElement(
          "header",
          null,
          React.createElement("p", null, model.label || "Report"),
          React.createElement("h1", null, model.title),
          model.published
            ? React.createElement("time", { dateTime: model.published }, model.published.slice(0, 10))
            : null,
        ),
        React.createElement("div", {
          className: "ssr-content-sections",
          dangerouslySetInnerHTML: { __html: model.bodyHtml || "" },
        }),
        model.modified
          ? React.createElement("meta", { itemProp: "dateModified", content: model.modified })
          : null,
      ),
    );
  }

  return React.createElement(
    SiteChrome,
    null,
    React.createElement(
      "main",
      { className: "initial-page", "data-server-rendered-page": "true" },
      React.createElement(
        "header",
        null,
        React.createElement("p", null, model.siteName || "Jaldi-Vidhi"),
        React.createElement("h1", null, model.title),
        React.createElement("p", null, model.description),
      ),
      React.createElement("div", {
        className: "ssr-content-sections",
        dangerouslySetInnerHTML: { __html: model.bodyHtml || "" },
      }),
    ),
  );
};

module.exports = { SsrContent };
